import { Label } from '@folio/components/forms/label/label';
import { Badge } from '@folio/components/ui/badge';
import { Group } from '@folio/components/ui/flex';
import { RadioGroup, RadioGroupItem } from '@folio/components/ui/radio-group';
import { Text, Title } from '@folio/components/ui/text';
import { ThemeIcon } from '@folio/components/ui/theme-icon';
import { cn } from '@folio/lib';
import type { CorrectPolicyDocumentFraudVerdictInputBody, DocumentFraudScoreDto } from 'korint-sdk';
import { InfoIcon, Loader2Icon, ShieldAlertIcon, TriangleAlertIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FRAUD_SEVERITY_TONE,
  FRAUD_VERDICTS,
  TONE_TEXT_CLASS,
  fraudSignalCategory,
  isFraudVerdict,
  sortFraudSignals,
} from './fraudStatus';
import { FraudStatusBadge } from './FraudStatusBadge';
import { CorrectionPopover, CorrectionTrace } from './CorrectionPopover';

type FraudVerdict = CorrectPolicyDocumentFraudVerdictInputBody['verdict'];

const SEVERITY_ICON = {
  critical: ShieldAlertIcon,
  warning: TriangleAlertIcon,
  info: InfoIcon,
};

export function FraudAnalysisSection({
  fraud,
  pending,
  onCorrectVerdict,
  isCorrectingVerdict,
}: {
  fraud?: DocumentFraudScoreDto | null;
  pending?: boolean;
  onCorrectVerdict?: (correction: CorrectPolicyDocumentFraudVerdictInputBody) => void;
  isCorrectingVerdict?: boolean;
}) {
  const { t } = useTranslation();
  const signals = fraud ? sortFraudSignals(fraud.signals) : [];
  const score =
    fraud && fraud.fraudScore !== null
      ? t('documentReview.fraud.scoreOutOf', { score: fraud.fraudScore })
      : undefined;

  return (
    <section className="rounded-lg border border-border p-4">
      <Group justify="between" gap={3} className="mb-3 flex-wrap">
        <Title order={3} size="lg" tone="default" className="leading-7">
          {t('documentReview.fraud.title')}
        </Title>
        <Group gap={1} className="shrink-0">
          {fraud && <FraudStatusBadge fraud={fraud} />}
          {fraud && onCorrectVerdict && fraud.status !== 'pending' && (
            <FraudVerdictPopover
              fraud={fraud}
              onSubmit={onCorrectVerdict}
              isPending={isCorrectingVerdict}
            />
          )}
        </Group>
      </Group>

      {!fraud ? (
        pending && (
          <Group gap={2} className="text-muted-foreground">
            <Loader2Icon className="h-4 w-4 animate-spin" />
            <Text size="sm" tone="muted">
              {t('documentReview.fraud.pending')}
            </Text>
          </Group>
        )
      ) : (
        <>
          {fraud.managerVerdict && (
            <div className="mb-4 space-y-1 rounded-md border border-border bg-muted/40 p-3">
              <Text size="sm" weight="semibold" tone="default">
                {t('documentReview.fraud.review.human')}
              </Text>
              {fraud.managerReviewedAt && (
                <CorrectionTrace
                  correctedAt={fraud.managerReviewedAt}
                  correctedByEmail={fraud.managerReviewedByEmail}
                  reason={fraud.managerReviewNotes ?? undefined}
                />
              )}
              <Text size="xs" tone="muted">
                {t('documentReview.fraud.review.automatedVerdict', {
                  status: [t(`documentReview.fraud.status.${fraud.status}`), score]
                    .filter(Boolean)
                    .join(' · '),
                })}
              </Text>
            </div>
          )}
          <Text size="sm" tone="default">
            {t(`documentReview.fraud.summary.${fraud.status}`)}
          </Text>
          {fraud.fieldExtractionStatus === 'empty' && (
            <Text size="sm" tone="muted" className="mt-2">
              {t('documentReview.fraud.limitedCoverage')}
            </Text>
          )}

          {signals.length > 0 ? (
            <>
              <Text
                size="xs"
                weight="semibold"
                tone="muted"
                className="-mx-5 mt-4 bg-muted/60 px-5 py-2 uppercase tracking-wide"
              >
                {t('documentReview.fraud.findings')}{' '}
                <span className="font-normal">{signals.length}</span>
              </Text>
              <ol className="-mx-5 divide-y divide-border">
                {signals.map(({ code, message, severity = 'info' }, index) => {
                  const severityTone = FRAUD_SEVERITY_TONE[severity];
                  const SeverityIcon = SEVERITY_ICON[severity];
                  const category = fraudSignalCategory(code);
                  return (
                    <li key={index} className="flex gap-3 px-5 py-3">
                      <ThemeIcon
                        variant="filled"
                        tone={severityTone}
                        size="sm"
                        radius="full"
                        className="text-xs font-semibold"
                      >
                        {index + 1}
                      </ThemeIcon>
                      <div className="min-w-0 space-y-1">
                        <Group gap={2} className="flex-wrap">
                          <Text
                            inline
                            size="sm"
                            weight="semibold"
                            className={cn(
                              'inline-flex items-center gap-1',
                              TONE_TEXT_CLASS[severityTone],
                            )}
                          >
                            <SeverityIcon className="size-4" />
                            {t(`documentReview.fraud.severity.${severity}`)}
                          </Text>
                          {category && (
                            <Badge variant="light" tone="muted" size="sm">
                              {t(`documentReview.fraud.category.${category}`)}
                            </Badge>
                          )}
                        </Group>
                        <Text size="sm" tone="default">
                          {message ?? code}
                        </Text>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          ) : (
            isFraudVerdict(fraud.status) && (
              <Text size="sm" tone="muted" className="mt-2">
                {t('documentReview.fraud.noFindings')}
              </Text>
            )
          )}
        </>
      )}
    </section>
  );
}

function FraudVerdictPopover({
  fraud,
  onSubmit,
  isPending,
}: {
  fraud: DocumentFraudScoreDto;
  onSubmit: (correction: CorrectPolicyDocumentFraudVerdictInputBody) => void;
  isPending?: boolean;
}) {
  const { t } = useTranslation();
  const [verdict, setVerdict] = useState<FraudVerdict | undefined>(
    fraud.managerVerdict ?? undefined,
  );
  const title = t('documentReview.correction.fraudTitle');

  return (
    <CorrectionPopover
      title={title}
      canSave={!!verdict}
      isPending={isPending}
      initialReason={fraud.managerReviewNotes ?? ''}
      onOpenChange={open => open && setVerdict(fraud.managerVerdict ?? undefined)}
      onSave={notes => verdict && onSubmit({ verdict, notes, expectedUpdatedAt: fraud.updatedAt })}
    >
      <RadioGroup
        value={verdict ?? ''}
        onValueChange={value => setVerdict(value as FraudVerdict)}
        aria-label={title}
      >
        {FRAUD_VERDICTS.map(option => (
          <Group key={option} gap={2}>
            <RadioGroupItem value={option} id={`fraud-verdict-${option}`} />
            <Label htmlFor={`fraud-verdict-${option}`}>
              {t(`documentReview.fraud.status.${option}`)}
            </Label>
          </Group>
        ))}
      </RadioGroup>
    </CorrectionPopover>
  );
}
